import React, { useCallback, useEffect, useState } from 'react';
import algoliasearch from 'algoliasearch/lite';
import {
  InstantSearch,
  Pagination,
  Configure,
  useMenu,
  useSearchBox,
  useHits,
  useInstantSearch,
  usePagination,
  useClearRefinements,
  useSortBy,
} from 'react-instantsearch';
import css from './SearchSection.module.css';
import RentalCard from './RentalCard/RentalCard';
import NoResult from './NoResult/NoResult';
import { H4, Loader } from '../../../components';
import IconCard from '../../../components/SavedCardDetails/IconCard/IconCard';
import classNames from 'classnames';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { parse } from '../../../util/urlHelpers';
import AddToCartModel from './AddToCartModal/AddToCartModel';
import { useDispatch, useSelector } from 'react-redux';
import { transformItemsToOriginalListings } from '../StoreProfilePage';
import { pathByRouteName } from '../../../util/routes';
import { storeDataToSession } from '../../../util/data';

export const SORT_DEFAULT_OPTION = 'order';
const ALGOLIA_CLIENT_ID = process.env.REACT_APP_ALGOLIA_APP_ID;
const ALGOLIA_API_KEY = process.env.REACT_APP_ALGOLIA_ADMIN_API_KEY;
const ALGOLIA_LISTINGS_INDEX = process.env.REACT_APP_ALGOLIA_LISTINGS_INDEX;

const searchClient = algoliasearch(ALGOLIA_CLIENT_ID, ALGOLIA_API_KEY);

function CustomHits(props) {
  const {
    intl,
    onUpdateRecordsOrder,
    updateOrderError,
    sortOption,
    storeUserName,
    config,
    onFetchTransactionLineItems,
    onFetchTimeSlots,
    onAddToCart,
    isCurrentUser,
    currentUser,
    exchangeRates,
    fetRatesError,
    fetchRatesInProgress,
    currentUserCurrency,
    routes,
    history
  } = props;
  const [renderableItems, setRenderableItems] = useState([]);
  const [cartModalId, setCartModalId] = useState(null);
  const { status, error } = useInstantSearch({ catchError: true });
  const { items } = useHits(props);

  const {
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
  } = useSelector(state => state.ListingPage);

  const {
    addToCartInProgress,
    addToCartError,
    notification
  } = useSelector(state => state.cart);
  const dispatch = useDispatch();


  useEffect(() => {
    setRenderableItems(
      sortOption === 'order' && items && items.length
        ? items.sort((a, b) => a.order - b.order)
        : (items || [])
    )
  }, [items, sortOption]);


  const onDragEnd = (result) => {
    const { destination, source } = result;

    if (!destination || !destination.index || destination.index === source.index) {
      return;
    };

    const destinationItem = renderableItems[destination.index];

    const updatedItems = renderableItems.map((i, ind) => {
      if (ind === source.index) {
        return {
          ...i,
          order: destinationItem.order
        }
      } else if (ind <= destination.index) {
        return {
          ...i,
          order: i.order - 1
        }
      }
      return i;
    })


    const sortedItems = updatedItems.sort((a, b) => a.order - b.order);

    setRenderableItems(sortedItems);

    const data = [...updatedItems].splice(source.index, destination.index + 1).map((item, index) => ({
      objectID: item.objectID,
      order: item.order
    }));

    onUpdateRecordsOrder(data);
  };

  if (status === 'stalled' || status === 'loading') {
    return <Loader />;
  }

  if (error) {
    return <H4>{error.message}</H4>;
  }

  if (!items.length && status === 'idle') {
    return <NoResult isCurrentUser={isCurrentUser} intl={intl} storeUserName={storeUserName} />;
  };


  const addToCartHandler = params => {
    // Check if item is of current user
    if (isCurrentUser) {
      window.scrollTo(0, 0);
    } else if (currentUser) {
      onAddToCart(config, params)
        .then(r => {
          const cartPagePath = pathByRouteName('CartPage', routes);
          history.push(cartPagePath);
        })
        .catch(() => {
          // do nothing redux will manage.
        });
    } else {
      // Save item to session storage and move user to cartPage.
      const data = {
        ...params,
        quantity: params.quantity ? Number(params.quantity) : null,
        units: params.units ? Number(params.units) : null,
      };
      storeDataToSession(data, 'cart');
      const cartPagePath = pathByRouteName('CartPage', routes);
      history.push(cartPagePath);
    }
  };

  if (isCurrentUser) {
    return (<DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable" direction='vertical'>
        {(provided) => (
          <div
            className={css.rentals}
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {renderableItems.map((item, index) => (
              <Draggable key={item.objectID} draggableId={item.objectID} index={index}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                  >
                    <RentalCard
                      rental={item}
                      allowDrag={isCurrentUser && sortOption === 'order'}
                      isCurrentUser
                      {...props}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>)
  };


  const modalListing = renderableItems.find(item => item.objectID === cartModalId);
  const modalSharetribeListing = modalListing && modalListing.objectID 
  ? transformItemsToOriginalListings([modalListing])[0]
  : null;

  return (
    <div className={css.rentals}>
      {modalListing && cartModalId
        ? (<AddToCartModel
          isOpen={!!cartModalId && modalSharetribeListing}
          onClose={() => setCartModalId(null)}
          listing={modalSharetribeListing}
          author={modalListing?.user}
          monthlyTimeSlots={monthlyTimeSlots}
          lineItems={lineItems}
          fetchLineItemsInProgress={fetchLineItemsInProgress}
          fetchLineItemsError={fetchLineItemsError}
          config={config}
          onFetchTransactionLineItems={onFetchTransactionLineItems}
          onFetchTimeSlots={onFetchTimeSlots}
          onAddToCart={addToCartHandler}
          currentUser={currentUser}
          exchangeRates={exchangeRates}
          fetRatesError={fetRatesError}
          fetchRatesInProgress={fetchRatesInProgress}
          currentUserCurrency={currentUserCurrency}
          routeConfiguration={routes}
          history={history}
          addToCartInProgress={addToCartInProgress}
          addToCartError={addToCartError}
          cartNotification={notification}
        />) : null}
      {renderableItems.map(item => (
        <RentalCard
          rental={item}
          allowDrag={isCurrentUser && sortOption === 'order'}
          setCartModalId={setCartModalId}
          dispatch={dispatch}
          {...props}
        />
      ))}
    </div>
  );
}

function MenuSelect(props) {
  const { items, refine } = useMenu(props);
  const { value: selectedValue } = items.find(item => item.isRefined) || {
    value: '',
  };

  const { canRefine, refine: clearRefine } = useClearRefinements(props);

  return (
    <ul className={css.categoryPills}>
      <li
        disabled={!canRefine}
        onClick={clearRefine}
        className={classNames(
          css.pill,
          selectedValue === '' ? css.active : css.inActive
        )}
      >
        All
      </li>
      {items.map(item => (
        <li
          key={item.value}
          className={classNames(
            css.pill,
            selectedValue === item.value ? css.active : css.inActive
          )}
          onClick={() => refine(item.value)}
        >
          {item.label}
        </li>
      ))}
    </ul>
  );
}

function CustomSearchBox({ searchInputText }) {
  const memoizedSearch = useCallback((query, search) => {
    search(query);
  }, []);

  const { refine } = useSearchBox({
    queryHook: memoizedSearch,
  });

  const handleChange = event => {
    refine(event.target.value);
  };

  return (
    <>
      <input
        onChange={handleChange}
        type="text"
        placeholder={searchInputText || 'Search by keyword'}
      />
    </>
  );
}

const SortBy = props => {
  const { sortValue, setSortValue } = props;
  const { options, refine } = useSortBy(props);
  const defaultOption = { label: 'Sort By: Order', value: SORT_DEFAULT_OPTION };
  return (
    <select
      onChange={event => {
        if (event.target.value !== SORT_DEFAULT_OPTION) {
          refine(event.target.value);
        }
        setSortValue(event.target.value);
      }}
      value={sortValue}
    >
      <option key={defaultOption.value} value={defaultOption.value}>
        {defaultOption.label}
      </option>
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

const SearchSectionMobile = ({ searchInputText, sortValue, setSortValue }) => {
  return (
    <div className={css.queryContainerMobile}>
      <div className={css.categorySelector}>
        <div className={css.keywordSearchMobile}>
          <CustomSearchBox searchInputText={searchInputText} />
          <SortBy
            items={[
              {
                label: 'Sort By: New Rentals',
                value: `${ALGOLIA_LISTINGS_INDEX}_createdAt_desc`,
              },
              {
                label: 'Sort By: Old Rentals',
                value: `${ALGOLIA_LISTINGS_INDEX}_createdAt_asc`,
              },
            ]}
            sortValue={sortValue}
            setSortValue={setSortValue}
          />
        </div>
        <div className={css.MenuSelect}>
          <MenuSelect attribute="publicData.category" />
        </div>
      </div>
    </div>
  );
};

const SearchSectionDesktop = ({ searchInputText, sortValue, setSortValue }) => {
  return (
    <div className={css.queryContainerDesktop}>
      <div className={css.categorySelector}>
        <div className={css.MenuSelect}>
          <MenuSelect attribute="publicData.category" />
        </div>
        <div className={css.rightSearch}>
          {/* <div className={css.sortSelect}>
            <SortBy
              items={[
                {
                  label: 'Sort By: New Rentals',
                  value: `${ALGOLIA_LISTINGS_INDEX}_createdAt_desc`,
                },
                {
                  label: 'Sort By: Old Rentals',
                  value: `${ALGOLIA_LISTINGS_INDEX}_createdAt_asc`,
                },
              ]}
              sortValue={sortValue}
              setSortValue={setSortValue}
            />
          </div> */}

          <div className={css.keywordSearch}>
            <CustomSearchBox searchInputText={searchInputText} />
          </div>
        </div>
      </div>
    </div>
  );
};

function SearchQuerySection(props) {
  return (
    <>
      <SearchSectionMobile {...props} />
      <SearchSectionDesktop {...props} />
    </>
  );
}

function PaginationItem({ isDisabled, href, onClick, isActive, ...props }) {
  if (isDisabled) {
    return (
      <li className={css.paginationItem}>
        <span {...props} />
      </li>
    );
  }

  const classes = isActive
    ? classNames(css.paginationItem, css.active)
    : css.paginationItem;
  return (
    <li>
      <a
        className={classes}
        href={href}
        onClick={event => {
          if (isModifierClick(event)) {
            return;
          }

          event.preventDefault();

          onClick(event);
        }}
        {...props}
      />
    </li>
  );
}

function isModifierClick(event) {
  const isMiddleClick = event.button === 1;

  return Boolean(
    isMiddleClick ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey
  );
}

function CustomPagination(props) {
  const {
    pages,
    currentRefinement,
    nbPages,
    isFirstPage,
    isLastPage,
    refine,
    createURL,
  } = usePagination(props);
  const firstPageIndex = 0;
  const previousPageIndex = currentRefinement - 1;
  const nextPageIndex = currentRefinement + 1;
  const lastPageIndex = nbPages - 1;

  if (pages.length === 1) return null;

  return (
    <ul className={css.pagination}>
      <PaginationItem
        className={css.arrow}
        isDisabled={isFirstPage}
        href={createURL(previousPageIndex)}
        onClick={() => refine(previousPageIndex)}
      >
        <IconCard brand="previous" />
      </PaginationItem>
      <div className={css.pageNumber}>
        {pages.map(page => {
          const label = page + 1;
          return (
            <PaginationItem
              key={page}
              isDisabled={false}
              aria-label={`Page ${label}`}
              href={createURL(page)}
              onClick={() => refine(page)}
              isActive={currentRefinement === page}
            >
              {label}
            </PaginationItem>
          );
        })}
      </div>

      <PaginationItem
        className={css.arrow}
        isDisabled={isLastPage}
        href={createURL(nextPageIndex)}
        onClick={() => refine(nextPageIndex)}
      >
        <IconCard brand="next" />
      </PaginationItem>
    </ul>
  );
}

export default function SearchSection(props) {
  const {
    params,
    routes,
    history,
    user,
    intl,
    onUpdateRecordsOrder,
    updateOrderError,
    config,
    isCurrentUser,
    currentUser,
    exchangeRates,
    fetRatesError,
    fetchRatesInProgress,
    currentUserCurrency,
    onFetchTransactionLineItems,
    onFetchTimeSlots,
    onAddToCart
  } = props;

  const { profile } = user?.attributes;
  const { displayName, publicData = {} } = profile;
  const { storeName } = publicData;

  const storeUserName = !!storeName ? storeName : displayName;

  const { id } = params;
  const [ownerId, setOwnerId] = useState(id);
  const [sortValue, setSortValue] = useState(SORT_DEFAULT_OPTION);

  useEffect(() => {
    setOwnerId(id);
  }, [id]);

  const searchInputText = intl.formatMessage(
    { id: 'StoreProfilePage.SearchSection.searchInputText' },
    { displayName: storeUserName }
  );

  return (
    <section className={css.searchSection}>
      <InstantSearch
        searchClient={searchClient}
        indexName={ALGOLIA_LISTINGS_INDEX}
      >
        <Configure
          hitsPerPage={32}
          filters={`user.id:${ownerId}`} // Filter listings where user.id matches userId
        />
        <SearchQuerySection
          searchInputText={searchInputText}
          sortValue={sortValue}
          setSortValue={setSortValue}
        />
        <CustomHits
          routes={routes}
          history={history}
          isCurrentUser={isCurrentUser}
          onUpdateRecordsOrder={onUpdateRecordsOrder}
          updateOrderError={updateOrderError}
          intl={intl}
          sortOption={sortValue}
          storeUserName={storeUserName}
          config={config}
          currentUser={currentUser}
          params={params}
          exchangeRates={exchangeRates}
          fetRatesError={fetRatesError}
          fetchRatesInProgress={fetchRatesInProgress}
          currentUserCurrency={currentUserCurrency}
          onFetchTransactionLineItems={onFetchTransactionLineItems}
          onFetchTimeSlots={onFetchTimeSlots}
          onAddToCart={onAddToCart}
        />
        <CustomPagination />
      </InstantSearch>
    </section>
  );
}
